/**
 * lib/parentLetterPdf.ts
 * ------------------------------------------------------------------
 * Parent-portal registration letters (parent-onboarding slice 5).
 *
 * One branded A4 letter per child, addressed generically to the
 * parent/guardian, carrying that child's single-use registration KEY and
 * the public form URL. Reuses the shared LKC PDF chrome (lib/pdfChrome:
 * drawLetterhead + stampFooters) so the letters match reports/mark-sheets.
 *
 * buildParentLetters(letters, opt) -> a jsPDF with one page per letter;
 * the caller does .save(name) (mirrors BulkPayslips' combined-PDF pattern).
 *
 * No QR dependency (none in the tree) — the URL is printed as text.
 * ------------------------------------------------------------------ */
import { jsPDF } from 'jspdf';
import {
  pageGeom,
  drawLetterhead,
  stampFooters,
  CONTENT_TOP,
  PDF,
  type PageGeom,
} from '@/lib/pdfChrome';
import { SCHOOL } from '@/lib/school.config';

export interface ParentLetter {
  studentName: string;
  className: string;
  key: string;
}

export interface ParentLetterOptions {
  logoDataUrl?: string | null;
  /** Public form URL, e.g. https://lkcportal.com/parent-key.html */
  formUrl: string;
  generatedBy?: string;
}

function renderLetter(
  pdf: jsPDF,
  geom: PageGeom,
  L: ParentLetter,
  opt: ParentLetterOptions,
): void {
  drawLetterhead(pdf, {
    title: 'PARENT PORTAL REGISTRATION',
    metaLine: L.className,
    logoDataUrl: opt.logoDataUrl,
    geom,
  });

  const width = geom.right - geom.MX;
  let y = CONTENT_TOP + 8;

  pdf.setTextColor(...PDF.INK);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(12);
  pdf.text(`Dear Parent / Guardian of ${L.studentName},`, geom.MX, y);
  y += 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...PDF.MUTED);
  pdf.text(`Class: ${L.className}`, geom.MX, y);
  y += 8;

  pdf.setTextColor(...PDF.INK);
  pdf.setFontSize(10.5);
  const intro =
    `${SCHOOL.name} now offers a Parent Portal where you can follow your ` +
    `child's academic progress, reports and attendance online. To register, ` +
    `use the unique key below — it links your account securely to your child.`;
  const introLines = pdf.splitTextToSize(intro, width);
  pdf.text(introLines, geom.MX, y);
  y += introLines.length * 5 + 6;

  // ---- prominent KEY box ----
  const boxH = 22;
  pdf.setDrawColor(...PDF.NAVY);
  pdf.setLineWidth(0.5);
  pdf.setFillColor(...PDF.BAND);
  pdf.roundedRect(geom.MX, y, width, boxH, 3, 3, 'FD');
  const cx = geom.MX + width / 2;
  pdf.setTextColor(...PDF.MUTED);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(8);
  pdf.text('YOUR REGISTRATION KEY', cx, y + 7, { align: 'center' });
  pdf.setTextColor(...PDF.NAVY);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(20);
  pdf.text(L.key, cx, y + 16, { align: 'center' });
  y += boxH + 10;

  // ---- how to register ----
  pdf.setTextColor(...PDF.INK);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.text('How to register', geom.MX, y);
  y += 6;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10.5);
  const steps = [
    `1.  On your phone or computer, go to:  ${opt.formUrl}`,
    '2.  Enter the registration key shown above.',
    "3.  Confirm your child's name, then register with your name and email.",
    '4.  The school will review and email your login invite.',
  ];
  for (const s of steps) {
    const sl = pdf.splitTextToSize(s, width - 2);
    pdf.text(sl, geom.MX, y);
    y += sl.length * 5 + 2;
  }
  y += 6;

  pdf.setTextColor(...PDF.MUTED);
  pdf.setFontSize(9.5);
  const note =
    'Please keep this key private — it is unique to your child and can be ' +
    'used only once. If you have any difficulty, please contact the school office.';
  const noteLines = pdf.splitTextToSize(note, width);
  pdf.text(noteLines, geom.MX, y);
  y += noteLines.length * 5 + 8;

  pdf.setTextColor(...PDF.INK);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10.5);
  pdf.text('Kind regards,', geom.MX, y);
  y += 5;
  pdf.setFont('helvetica', 'bold');
  pdf.text(SCHOOL.name, geom.MX, y);
}

/** Build a combined A4 PDF, one letter per child. Caller saves it. */
export function buildParentLetters(
  letters: ParentLetter[],
  opt: ParentLetterOptions,
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const geom = pageGeom('p');
  letters.forEach((L, i) => {
    if (i > 0) pdf.addPage();
    renderLetter(pdf, geom, L, opt);
  });
  stampFooters(pdf, { geom, generatedBy: opt.generatedBy });
  return pdf;
}
